import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React, { useCallback, useContext, useState } from 'react';
import {
  Box,
  ButtonGroup,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import { Delete, PlayArrow, Save } from '@material-ui/icons';
import { useQuery } from 'react-query';
import { Result } from 'types';
import fetch from 'utils/fetch';
import { useSearchContext } from './context/search-context';
import { NewCentralFormType, SavedParamsDto } from './types';
import { AuthCtx } from 'components/auth/auth-ctx';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';

export const SAVED_FILTERS_VERSION = 8;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: 'calc(100vh - 140px)',
      height: '100%',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
    },
    title: {
      padding: theme.spacing(2),
    },
  })
);

export function GridRightPanel() {
  const classes = useStyles();
  const auth = useContext(AuthCtx);
  if (!auth.userInfo?.aisAuthenticated) {
    return <div className={classes.root}></div>;
  }
  return <GridRightPanelAuth />;
}

const useSavedParams = () =>
  useQuery('saved-params', () =>
    fetch('/api/search/my-saved-params', {
      method: 'POST',
      body: JSON.stringify({
        size: -1,
        sort: [{ field: 'added', order: 'DESC' }],
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    }).then(async (r) => (await r.json()) as Result<SavedParamsDto>)
  );

export function GridRightPanelAuth() {
  const classes = useStyles();
  const { currentFormValues, resetForm } = useSearchContext();
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  const { data, isLoading, refetch } = useSavedParams();

  const handleSaveFilters = useCallback(async () => {
    const body = {
      label: newLabel,
      params: JSON.stringify(currentFormValues),
    };
    await fetch('/api/search/save-params', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    refetch();
  }, [currentFormValues, refetch, newLabel]);

  const handleDeleteFilter = useCallback(
    async (id: string) => {
      setLoading(true);
      await fetch('/api/search/save-params/' + id, {
        method: 'DELETE',
        headers: new Headers({ 'Content-Type': 'application/json' }),
      });
      refetch();
      setLoading(false);
    },
    [refetch]
  );

  const { showNotification } = useContext(NotificationCtx);

  return (
    <div className={classes.root}>
      <Box
        display="flex"
        alignItems="stretch"
        justifyContent="space-between"
        style={{ marginBottom: 8, marginTop: 32, width: '100%', padding: 4 }}
      >
        <ButtonGroup style={{ width: '100%' }}>
          <TextField
            style={{ flexGrow: 1, padding: 0 }}
            variant="outlined"
            label="Jméno nového filtru"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            //disabled={!auth.userInfo?.aisAuthenticated}
          />
          <Button
            disabled={newLabel === '' || loading || isLoading}
            variant="contained"
            size="small"
            color="primary"
            onClick={handleSaveFilters}
            style={{ marginLeft: 4, padding: 2, margin: 0 }}
          >
            <Save />
          </Button>
        </ButtonGroup>
      </Box>
      <Typography variant="h6" className={classes.title}>
        Uložené filtry
      </Typography>
      {loading || isLoading ? (
        <div style={{ padding: 8, width: '100%' }}>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ padding: 8, width: '100%' }}>
          {(data?.items ?? []).map((p, index) => {
            return (
              <Box key={`${index}-${p.id}`} style={{ marginBottom: 12 }}>
                <Box
                  display="flex"
                  alignItems="stretch"
                  justifyContent="space-between"
                >
                  <ButtonGroup style={{ width: '100%' }}>
                    <Button
                      style={{ padding: 4 }}
                      key={p.id}
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => {
                        const values = JSON.parse(
                          p.params
                        ) as NewCentralFormType;

                        if (values.version !== SAVED_FILTERS_VERSION) {
                          showNotification(
                            `Nepodporovaná verze nastavení (verze: ${
                              values.version ?? 0
                            }, požadovaná verze: ${SAVED_FILTERS_VERSION})`,
                            NotificationVariant.ERROR
                          );
                          return;
                        }
                        if (values) {
                          resetForm?.current?.({ values });
                        }
                      }}
                    >
                      <PlayArrow />
                    </Button>
                    <div
                      style={{
                        border: '1px solid #AAAAAA',
                        borderRadius: 4,
                        flexGrow: 1,
                        padding: 4,
                      }}
                    >
                      <Typography style={{ fontSize: 14 }}>
                        {p.label}
                      </Typography>
                    </div>

                    <Button
                      style={{ padding: 4 }}
                      key={p.id}
                      variant="outlined"
                      size="small"
                      color="secondary"
                      onClick={() => {
                        handleDeleteFilter(p.id);
                      }}
                    >
                      <Delete />
                    </Button>
                  </ButtonGroup>
                </Box>
                <Typography
                  style={{ fontSize: 10, textAlign: 'left', color: '#AAAAAA' }}
                >
                  {new Date(p.added).toLocaleDateString('cs-CZ')} -{' '}
                  {new Date(p.added).toLocaleTimeString('cs-CZ')}
                </Typography>
              </Box>
            );
          })}
        </div>
      )}
    </div>
  );
}
